// ═══════════════════════════════════════════════════════════════
// Impact — Public Self-Report Page
// Citizens report property damage (no auth required)
// Route: /self-report/:incidentId
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, TextInput, Textarea, Select, Button,
  Group, Stack, Divider, Alert, Badge, Radio, FileButton, Image,
  ActionIcon, Checkbox, Stepper, Loader, ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle, IconCheck, IconChevronLeft, IconChevronRight,
  IconHome, IconSend, IconTrash, IconUpload, IconPhoto,
  IconUser, IconPhone, IconMail, IconMapPin, IconBuilding,
  IconUsers, IconHomeBolt,
} from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { DAMAGE_QUESTIONS, calculateScore, scoreToCategory } from './lib/damageScore';

// ── Types ─────────────────────────────────────────────────────

interface Portal {
  enabled: boolean;
  incidentId: string;
}

interface AddressResult {
  display_name: string;
  address?: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
  };
}

const DWELLING_TYPES = [
  { value: 'Single-family', label: 'Single-family' },
  { value: 'Multi-family', label: 'Multi-family' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Mobile Home', label: 'Mobile Home' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Condo', label: 'Condo' },
];

const LIVING_OPTIONS = [
  { value: 'home', label: 'Still in my home', icon: IconHome },
  { value: 'family', label: 'With family/friends', icon: IconUsers },
  { value: 'shelter', label: 'At a shelter', icon: IconHomeBolt },
  { value: 'hotel', label: 'Hotel/motel', icon: IconBuilding },
  { value: 'vehicle', label: 'In my vehicle', icon: IconMapPin },
  { value: 'other', label: 'Other', icon: IconMapPin },
];

const STATE_MAP: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO',
  Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
  Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',
  Wyoming: 'WY',
};

function stateAbbr(s: string): string {
  return STATE_MAP[s] || s.substring(0, 2).toUpperCase();
}

// ── Public Shell (standalone layout for unauthenticated pages) ─

function PublicShell({ children, orgName, disasterName }: { children: React.ReactNode; orgName?: string; disasterName?: string }) {
  return (
    <Box maw={720} mx="auto" px="md" py="lg" style={{ minHeight: '100vh' }}>
      <Paper
        radius="lg"
        p="xl"
        mb="lg"
        style={{
          background: 'linear-gradient(135deg, #DF461B 0%, var(--color-error) 50%, var(--color-primary-800) 100%)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,.08) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            pointerEvents: 'none',
          }}
        />
        <Title order={2} c="white" style={{ position: 'relative', zIndex: 1 }}>
          Report Property Damage
        </Title>
        <Text c="rgba(255,255,255,.75)" size="sm" mt={4} style={{ position: 'relative', zIndex: 1 }}>
          {disasterName
            ? `Reporting for: ${disasterName}`
            : orgName
              ? `${orgName} — Damage Assessment`
              : 'Help your community by reporting damage to your property'}
        </Text>
      </Paper>
      {children}
      <Text ta="center" size="xs" c="dimmed" mt="lg">
        Impact Damage Assessment Tool — Powered by OpenGov EMC
      </Text>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────

export default function SelfReport() {
  const { incidentId } = useParams<{ incidentId: string }>();

  // Portal state
  const [portal, setPortal] = useState<Portal | null>(null);
  const [portalLoading, setPortalLoading] = useState(true);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Stepper
  const [active, setActive] = useState(0);

  // Step 1: Contact / Property
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [dwellingType, setDwellingType] = useState('');
  const [occupants, setOccupants] = useState('');

  // Address autocomplete
  const [addrResults, setAddrResults] = useState<AddressResult[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 2: Damage questions
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [livingSituation, setLivingSituation] = useState('');
  const [hasInsurance, setHasInsurance] = useState<string | null>(null);
  const [hasFloodInsurance, setHasFloodInsurance] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  // Step 3: Photos & consent
  const [photos, setPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [consent, setConsent] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportRef, setReportRef] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Portal loading ─────────────────────────────────────────

  useEffect(() => {
    if (incidentId) {
      setPortal({ enabled: true, incidentId });
      setPortalLoading(false);
    } else {
      setPortalError('No incident ID provided.');
      setPortalLoading(false);
    }
  }, [incidentId]);

  // ── Address autocomplete ───────────────────────────────────

  const searchAddress = useCallback((q: string) => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (!q || q.length < 3) {
      setAddrResults([]);
      setAddrOpen(false);
      return;
    }
    addrTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=us&limit=5&addressdetails=1`,
          { headers: { 'User-Agent': 'OGEMC-Impact/1.0' } },
        );
        const results: AddressResult[] = await r.json();
        setAddrResults(results);
        setAddrOpen(results.length > 0);
      } catch {
        /* ignore */
      }
    }, 350);
  }, []);

  const selectAddress = (result: AddressResult) => {
    const a = result.address;
    if (a) {
      setAddress(`${a.house_number || ''} ${a.road || ''}`.trim());
      setCity(a.city || a.town || a.village || '');
      setState(a.state ? stateAbbr(a.state) : '');
      setZip(a.postcode || '');
    } else {
      setAddress(result.display_name || '');
    }
    setAddrOpen(false);
  };

  // ── Damage score ───────────────────────────────────────────

  const score = calculateScore(answers);
  const { category, label: catLabel, color: catColor, description: catDesc } = scoreToCategory(score);
  const allQuestionsAnswered = DAMAGE_QUESTIONS.every(q => answers[q.id] !== undefined);

  // ── Photo handling (display only, not uploaded) ────────────

  const addPhotos = (files: File[]) => {
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 10));
  };

  const removePhoto = (idx: number) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  // ── Step validation ────────────────────────────────────────

  const canAdvance = (step: number): boolean => {
    if (step === 0) {
      return !!(name.trim() && phone.trim() && address.trim() && city.trim() && zip.trim());
    }
    if (step === 1) {
      return allQuestionsAnswered && !!livingSituation;
    }
    if (step === 2) {
      return consent;
    }
    return true;
  };

  const nextStep = () => {
    if (!canAdvance(active)) return;
    setActive(a => Math.min(a + 1, 2));
  };

  const prevStep = () => setActive(a => Math.max(a - 1, 0));

  // ── Submit ─────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!consent) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const reportId = await useImpactStore.getState().createSelfReport(
        {
          incidentId: incidentId || '',
          reporterName: name.trim(),
          reporterEmail: email.trim().toLowerCase() || '',
          reporterPhone: phone.trim() || '',
          address: address.trim(),
          city: city.trim(),
          stateCode: state.trim(),
          zip: zip.trim(),
          dwellingType: dwellingType || '',
          occupants: occupants || '',
        },
        {
          damageCategory: category,
          damageScore: String(score),
          insured: hasInsurance != null ? String(hasInsurance) : '',
          floodInsurance: hasFloodInsurance != null ? String(hasFloodInsurance) : '',
          livingSituation: livingSituation || '',
          notes: notes.trim() || '',
          consent: String(consent),
          reviewStatus: 'pending',
        },
      );

      setReportRef(`SR-${reportId}`);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit report.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset ──────────────────────────────────────────────────

  const resetForm = () => {
    setActive(0);
    setName(''); setPhone(''); setEmail('');
    setAddress(''); setCity(''); setState(''); setZip('');
    setDwellingType(''); setOccupants('');
    setAnswers({}); setLivingSituation('');
    setHasInsurance(null); setHasFloodInsurance(null); setNotes('');
    photos.forEach(p => URL.revokeObjectURL(p.preview));
    setPhotos([]); setConsent(false);
    setSubmitted(false); setReportRef(''); setSubmitError(null);
  };

  // ── Loading / Error states ─────────────────────────────────

  if (portalLoading) {
    return (
      <PublicShell>
        <Paper p="xl" radius="md" withBorder ta="center">
          <Loader size="lg" />
          <Text mt="md" c="dimmed">Loading...</Text>
        </Paper>
      </PublicShell>
    );
  }

  if (portalError || !portal) {
    return (
      <PublicShell>
        <Alert color="red" title="Unavailable" icon={<IconAlertTriangle size={20} />}>
          {portalError || 'Public damage reporting is not currently active.'}
        </Alert>
      </PublicShell>
    );
  }

  if (!portal.enabled) {
    return (
      <PublicShell>
        <Alert color="yellow" title="Not Active" icon={<IconAlertTriangle size={20} />}>
          Public damage reporting is not currently active for this incident. Please check back later.
        </Alert>
      </PublicShell>
    );
  }

  // ── Success screen ─────────────────────────────────────────

  if (submitted) {
    return (
      <PublicShell>
        <Paper p="xl" radius="md" withBorder ta="center">
          <ThemeIcon size={80} radius="xl" color="green" variant="light" mx="auto">
            <IconCheck size={40} />
          </ThemeIcon>
          <Title order={3} mt="md">Report Submitted Successfully</Title>
          <Text c="dimmed" mt="sm">
            Thank you for reporting your property damage. Your report has been received
            and will be reviewed by damage assessment teams. You may be contacted for follow-up.
          </Text>
          {reportRef && (
            <Text fw={600} c="orange" mt="md" size="lg">
              Reference #: {reportRef}
            </Text>
          )}
          <Button mt="xl" onClick={resetForm} variant="light" color="orange">
            Submit Another Report
          </Button>
        </Paper>
      </PublicShell>
    );
  }

  // ── Main Form ──────────────────────────────────────────────

  return (
    <PublicShell>
      <Stepper
        active={active}
        onStepClick={setActive}
        color="orange"
        mb="lg"
        allowNextStepsSelect={false}
      >
        {/* ── Step 1: Contact & Property ───────────────────── */}
        <Stepper.Step label="Your Info" description="Contact & property">
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb={4}>
              <IconUser size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Your Information
            </Title>
            <Text size="sm" c="dimmed" mb="md">
              This helps us follow up if needed
            </Text>

            <Stack gap="sm">
              <Group grow>
                <TextInput
                  label="Full Name"
                  placeholder="Your name"
                  required
                  value={name}
                  onChange={e => setName(e.currentTarget.value)}
                  leftSection={<IconUser size={16} />}
                />
                <TextInput
                  label="Phone"
                  placeholder="(555) 555-1234"
                  required
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.currentTarget.value)}
                  leftSection={<IconPhone size={16} />}
                />
              </Group>

              <TextInput
                label="Email"
                placeholder="email@example.com (optional)"
                type="email"
                value={email}
                onChange={e => setEmail(e.currentTarget.value)}
                leftSection={<IconMail size={16} />}
              />

              <Box pos="relative">
                <TextInput
                  label="Property Address"
                  placeholder="Start typing your address..."
                  required
                  value={address}
                  onChange={e => {
                    setAddress(e.currentTarget.value);
                    searchAddress(e.currentTarget.value);
                  }}
                  onBlur={() => setTimeout(() => setAddrOpen(false), 200)}
                  leftSection={<IconMapPin size={16} />}
                />
                {addrOpen && addrResults.length > 0 && (
                  <Paper
                    pos="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    shadow="md"
                    radius="sm"
                    style={{ zIndex: 600, maxHeight: 180, overflowY: 'auto' }}
                    withBorder
                  >
                    {addrResults.map((r, i) => (
                      <Box
                        key={i}
                        p="xs"
                        style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                        onMouseDown={() => selectAddress(r)}
                      >
                        <Text size="sm" lineClamp={1}>{r.display_name}</Text>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>

              <Group grow>
                <TextInput
                  label="City"
                  placeholder="City"
                  required
                  value={city}
                  onChange={e => setCity(e.currentTarget.value)}
                />
                <TextInput
                  label="State"
                  placeholder="e.g., FL"
                  maxLength={2}
                  value={state}
                  onChange={e => setState(e.currentTarget.value.toUpperCase())}
                  style={{ maxWidth: 100 }}
                />
              </Group>

              <Group grow>
                <TextInput
                  label="ZIP Code"
                  placeholder="12345"
                  required
                  maxLength={10}
                  value={zip}
                  onChange={e => setZip(e.currentTarget.value)}
                />
                <Select
                  label="Dwelling Type"
                  placeholder="Select..."
                  data={DWELLING_TYPES}
                  value={dwellingType || null}
                  onChange={v => setDwellingType(v || '')}
                  leftSection={<IconBuilding size={16} />}
                  clearable
                />
              </Group>

              <TextInput
                label="Number of Occupants"
                placeholder="How many people live here?"
                type="number"
                min={0}
                value={occupants}
                onChange={e => setOccupants(e.currentTarget.value)}
                leftSection={<IconUsers size={16} />}
              />
            </Stack>

            <Group mt="lg" justify="flex-end">
              <Button
                onClick={nextStep}
                disabled={!canAdvance(0)}
                rightSection={<IconChevronRight size={16} />}
                color="orange"
              >
                Next — Damage Details
              </Button>
            </Group>
          </Paper>
        </Stepper.Step>

        {/* ── Step 2: Damage Questions ─────────────────────── */}
        <Stepper.Step label="Damage" description="Describe damage">
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb={4}>
              Describe the Damage
            </Title>
            <Text size="sm" c="dimmed" mb="md">
              Select the option that best describes your situation for each question
            </Text>

            <Stack gap="lg">
              {DAMAGE_QUESTIONS.map(q => (
                <Box key={q.id}>
                  <Text fw={600} size="sm" mb="xs">
                    {q.label}
                  </Text>
                  <Radio.Group
                    value={answers[q.id] !== undefined ? String(answers[q.id]) : ''}
                    onChange={val =>
                      setAnswers(prev => ({ ...prev, [q.id]: Number(val) }))
                    }
                  >
                    <Stack gap={6}>
                      {q.options.map(opt => (
                        <Radio
                          key={opt.value}
                          value={String(opt.value)}
                          label={opt.label}
                          styles={{
                            radio: { cursor: 'pointer' },
                            label: { cursor: 'pointer' },
                          }}
                        />
                      ))}
                    </Stack>
                  </Radio.Group>
                </Box>
              ))}

              <Divider />

              {/* Living situation */}
              <Box>
                <Text fw={600} size="sm" mb="xs">
                  Where are you currently staying?
                </Text>
                <Group gap="xs">
                  {LIVING_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const selected = livingSituation === opt.value;
                    return (
                      <Paper
                        key={opt.value}
                        p="sm"
                        radius="md"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          borderColor: selected ? '#DF461B' : undefined,
                          background: selected ? 'rgba(223,70,27,0.06)' : undefined,
                          textAlign: 'center',
                          minWidth: 100,
                          flex: '1 1 100px',
                        }}
                        onClick={() => setLivingSituation(opt.value)}
                      >
                        <Icon size={22} color={selected ? '#DF461B' : undefined} style={{ margin: '0 auto' }} />
                        <Text size="xs" fw={600} mt={4}>{opt.label}</Text>
                      </Paper>
                    );
                  })}
                </Group>
              </Box>

              <Divider />

              {/* Insurance */}
              <Group grow>
                <Select
                  label="Homeowner Insurance?"
                  placeholder="Select..."
                  data={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                    { value: 'unknown', label: 'Not sure' },
                  ]}
                  value={hasInsurance}
                  onChange={setHasInsurance}
                  clearable
                />
                <Select
                  label="Flood Insurance?"
                  placeholder="Select..."
                  data={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                    { value: 'unknown', label: 'Not sure' },
                  ]}
                  value={hasFloodInsurance}
                  onChange={setHasFloodInsurance}
                  clearable
                />
              </Group>

              {/* Notes */}
              <Textarea
                label="Additional Details"
                placeholder="Describe any specific damage, safety concerns, or needs..."
                rows={3}
                value={notes}
                onChange={e => setNotes(e.currentTarget.value)}
              />

              {/* Score preview */}
              {allQuestionsAnswered && (
                <Alert color={catColor} variant="light" title="Preliminary Assessment">
                  <Group>
                    <Badge color={catColor} size="lg">{catLabel}</Badge>
                    <Text size="sm">{catDesc}</Text>
                  </Group>
                </Alert>
              )}
            </Stack>

            <Group mt="lg" justify="space-between">
              <Button
                variant="light"
                color="gray"
                onClick={prevStep}
                leftSection={<IconChevronLeft size={16} />}
              >
                Back
              </Button>
              <Button
                onClick={nextStep}
                disabled={!canAdvance(1)}
                rightSection={<IconChevronRight size={16} />}
                color="orange"
              >
                Next — Review & Submit
              </Button>
            </Group>
          </Paper>
        </Stepper.Step>

        {/* ── Step 3: Photos, Consent & Submit ─────────────── */}
        <Stepper.Step label="Review" description="Photos & submit">
          <Paper p="lg" radius="md" withBorder>
            <Title order={4} mb="md">
              Review & Submit
            </Title>

            {/* Photos (display only, not uploaded) */}
            <Box mb="lg">
              <Text fw={600} size="sm" mb="xs">
                <IconPhoto size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Photos (optional)
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                Add photos of the damage for your reference. Photos are not uploaded with this report.
              </Text>
              <Group gap="xs" mb="xs">
                {photos.map((p, i) => (
                  <Box key={i} pos="relative" w={90} h={90}>
                    <Image src={p.preview} w={90} h={90} radius="sm" fit="cover" />
                    <ActionIcon
                      size="xs"
                      color="red"
                      variant="filled"
                      pos="absolute"
                      top={2}
                      right={2}
                      onClick={() => removePhoto(i)}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Box>
                ))}
              </Group>
              {photos.length < 10 && (
                <FileButton onChange={files => files && addPhotos(Array.isArray(files) ? files : [files])} accept="image/*" multiple>
                  {(props) => (
                    <Button variant="light" color="orange" size="xs" leftSection={<IconUpload size={14} />} {...props}>
                      Add Photos
                    </Button>
                  )}
                </FileButton>
              )}
            </Box>

            <Divider mb="md" />

            {/* Summary */}
            <Box mb="lg">
              <Text fw={600} size="sm" mb="sm">Summary</Text>
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                }}
              >
                <SummaryField label="Name" value={name} />
                <SummaryField label="Phone" value={phone} />
                <SummaryField label="Email" value={email || '---'} />
                <SummaryField label="Address" value={address} />
                <SummaryField label="City/State/ZIP" value={`${city}, ${state} ${zip}`} />
                <SummaryField label="Dwelling" value={dwellingType || '---'} />
                <SummaryField label="Occupants" value={occupants || '---'} />
                <SummaryField label="Damage Level" value={catLabel} />
                <SummaryField label="Score" value={`${score} pts`} />
                <SummaryField label="Living Situation" value={livingSituation || '---'} />
                <SummaryField label="Insurance" value={hasInsurance || '---'} />
                <SummaryField label="Flood Ins." value={hasFloodInsurance || '---'} />
              </Box>
              {notes && (
                <Box mt="sm">
                  <Text size="xs" tt="uppercase" c="dimmed" fw={600}>Notes</Text>
                  <Paper p="xs" bg="gray.0" radius="sm" mt={4}>
                    <Text size="sm">{notes}</Text>
                  </Paper>
                </Box>
              )}
            </Box>

            <Divider mb="md" />

            {/* Consent */}
            <Paper p="sm" radius="sm" withBorder bg="orange.0" mb="md">
              <Checkbox
                checked={consent}
                onChange={e => setConsent(e.currentTarget.checked)}
                label="I certify that the information provided is true and accurate to the best of my knowledge. I understand this report will be used for preliminary damage assessment purposes and consent to being contacted by emergency management officials regarding this report."
                styles={{ label: { fontSize: 13 } }}
              />
            </Paper>

            {submitError && (
              <Alert color="red" mb="md" icon={<IconAlertTriangle size={16} />}>
                {submitError}
              </Alert>
            )}

            <Group mt="lg" justify="space-between">
              <Button
                variant="light"
                color="gray"
                onClick={prevStep}
                leftSection={<IconChevronLeft size={16} />}
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!consent || submitting}
                loading={submitting}
                leftSection={<IconSend size={16} />}
                color="orange"
              >
                Submit Report
              </Button>
            </Group>
          </Paper>
        </Stepper.Step>
      </Stepper>
    </PublicShell>
  );
}

// ── Summary field helper ─────────────────────────────────────

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <Box py={4}>
      <Text size="xs" tt="uppercase" c="dimmed" fw={600}>{label}</Text>
      <Text size="sm" fw={500}>{value}</Text>
    </Box>
  );
}
